/* eslint react-hooks/exhaustive-deps: "warn" */

import { Fragment, useContext, useMemo } from 'react';

import cn from 'classnames';

import { Link } from 'libs/router/Link';
import { Page } from 'libs/http/api/common/common.types';

import { ContentContext } from 'context/ContentContext';

import { useInt } from 'hooks/useInt';

import { Image } from 'ui/atoms/Image';
import { Icon } from 'ui/atoms/Icon/Icon';
import { Badge } from 'ui/atoms/Badge/Badge';
import type { BadgeVariant } from 'ui/atoms/Badge/Badge.types';

const blogTypeVariant: Record<string, BadgeVariant> = {
  company_registry_guides: 'indigo',
  kyb_compliance: 'rose',
  ownership_corporate_structure: 'violet',
  financials_credit_risk: 'emerald',
  company_data_ai: 'cyan',
  tax_regulatory_insights: 'orange',
  platforms_comparisons: 'warning',
  global_database_updates: 'primary',
  top_gdb_companies: 'success',
};

const caseStudyTypeVariant: Record<string, BadgeVariant> = {
  category_1: 'orange',
  category_2: 'indigo',
  category_3: 'emerald',
};

const getBadgeVariant = (article: NonNullable<Page['has_article']>): BadgeVariant =>
  article.case_studies_type
    ? (caseStudyTypeVariant[article.case_studies_type] ?? 'primary')
    : (blogTypeVariant[article.type ?? ''] ?? 'primary');

export interface Article {
  title: string;
  type: string;
  short_description: string;
  img: string;
  content: string;
}

interface Props {
  hasPriortyFirstImage?: boolean;
  posts: Page[];
  hasBadge?: boolean;
}

export const Blog = ({ hasPriortyFirstImage = false, posts = [], hasBadge = true }: Props) => {
  const { t } = useInt();

  const { pageVars } = useContext(ContentContext);

  const commonPageVars = useMemo(() => pageVars.common || {}, [pageVars.common]);

  const getLabel = (article: NonNullable<Page['has_article']>) =>
    article.case_studies_type
      ? commonPageVars[`case_studies_${article.case_studies_type}`]
      : commonPageVars[`blog_${article.type}`];

  const featuredPost = posts[0];
  const sideItems = posts.slice(1).filter((p) => p.has_article);

  if (!featuredPost?.has_article) {
    return null;
  }

  return (
    <div className="benefits-blog">
      <Link className="benefits-blog-left" route={featuredPost.slug} ariaLabel={featuredPost.title}>
        {featuredPost.background && (
          <div
            className={cn('benefits-blog-image', {
              'benefits-blog-image--contain': featuredPost.has_article.case_studies_type,
            })}
          >
            <Image
              fill
              style={{ objectFit: 'contain' }}
              width={700}
              height={400}
              src={featuredPost.background.url}
              alt={featuredPost.title}
              priority={hasPriortyFirstImage}
            />
          </div>
        )}

        <div className="benefits-blog-content">
          <div className="benefits-blog-header">
            <div className="benefits-blog-meta">
              {hasBadge && (
                <Badge variant={getBadgeVariant(featuredPost.has_article)} size="sm" className="benefits-blog-badge">
                  {getLabel(featuredPost.has_article)}
                </Badge>
              )}

              {featuredPost.has_article.read_time && (
                <div className="benefits-blog-read-time">
                  <Icon type="duration" size="small" />

                  <span>
                    {featuredPost.has_article.read_time} {commonPageVars.min_read || t('min read')}
                  </span>
                </div>
              )}
            </div>

            <h2 className="benefits-blog-title">{featuredPost.title}</h2>
          </div>
        </div>
      </Link>

      {sideItems.length > 0 && (
        <div className="benefits-blog-right">
          {sideItems.map((item, idx) =>
            item.has_article ? (
              <Fragment key={item.id}>
                {idx > 0 && <div className="benefits-blog-sep" />}

                <Link route={item.slug} tabIndex={0} className="benefits-blog-item">
                  {(loading) => (
                    <>
                      {item.background && (
                        <div
                          className={cn('benefits-blog-thumb', {
                            'benefits-blog-thumb--contain': item.has_article!.case_studies_type,
                          })}
                        >
                          <Image
                            fill
                            style={{ objectFit: 'contain' }}
                            width={128}
                            height={90}
                            src={item.background.url}
                            alt={item.title}
                          />
                        </div>
                      )}

                      <div className="benefits-blog-item-content">
                        {hasBadge && (
                          <Badge variant={getBadgeVariant(item.has_article!)} size="sm" className="benefits-blog-badge">
                            {getLabel(item.has_article!)}
                          </Badge>
                        )}

                        <h3 className={cn('benefits-blog-item-title', loading && 'loading')}>{item.title}</h3>
                      </div>
                    </>
                  )}
                </Link>
              </Fragment>
            ) : null,
          )}
        </div>
      )}
    </div>
  );
};
